(() => {
  if (customElements.get('ug-cart-klarna')) return;

  const documentReady = document.readyState === 'loading'
    ? new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }))
    : Promise.resolve();

  (() => {
    if (!document.querySelector('ug-cart-klarna') || window.KlarnaWebSDKScriptLoaded || customElements.get('klarna-placement')) return;
    const selector = 'script[src*="js.klarna.com/web-sdk/"]';
    const prepareLocale = (script) => {
      if (!script.dataset.locale && !window.KlarnaWebSDKScriptLoaded) {
        // Keep the App's locale and initialization order. The SDK also needs a
        // default locale for cart placements constructed after it has loaded.
        script.dataset.locale = document.querySelector('klarna-placement[data-locale]')?.dataset.locale || 'de-DE';
      }
    };
    const existing = document.querySelector(selector);
    if (existing) {
      prepareLocale(existing);
      return;
    }
    const observer = new MutationObserver((records) => {
      for (const { addedNodes } of records) {
        for (const node of addedNodes) {
          if (node.nodeType !== 1) continue;
          const script = node.matches(selector) ? node : node.querySelector(selector);
          if (!script) continue;
          prepareLocale(script);
          observer.disconnect();
          return;
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  })();

  let sdkPromise;
  const loadSDK = (clientId) => {
    if (customElements.get('klarna-placement')) return Promise.resolve();
    if (sdkPromise) return sdkPromise;

    sdkPromise = documentReady.then(() => new Promise((resolve, reject) => {
      if (customElements.get('klarna-placement')) {
        resolve();
        return;
      }
      let script = document.querySelector('script[src*="js.klarna.com/web-sdk/"]');
      // The App sets the PDP locale and price before it injects the SDK. Starting
      // it earlier upgrades an incomplete PDP placement, which can stay blank.
      const appOwnsSDK = document.querySelector('klarna-placement')
        || window.appBlockPlacements?.length
        || window.klarnaAppBlocksManager?.placements?.length
        || window.KlarnaWebSDKScriptLoaded
        || window.KlarnaOnsiteService;
      if (!script && !appOwnsSDK) {
        if (!clientId) {
          reject(new Error('Klarna public client ID is not configured'));
          return;
        }
        script = document.createElement('script');
        script.id = 'klarna-osm-script-tag';
        script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js';
        script.async = true;
        script.dataset.clientId = clientId;
        script.dataset.environment = 'production';
        script.dataset.locale = 'de-DE';
        script.dataset.ugCartSdk = 'true';
        script.addEventListener('error', () => {
          // Retry only an outer script request that definitely never started.
          if (!window.KlarnaWebSDKScriptLoaded && !customElements.get('klarna-placement')) {
            script.remove();
            sdkPromise = undefined;
          }
          reject(new Error('Klarna SDK could not load'));
        }, { once: true });
        document.head.append(script);
      }
      // A UI timeout must not remove/reload the SDK: removing its script cannot
      // cancel an in-flight module import and a retry would create a second SDK.
      customElements.whenDefined('klarna-placement').then(resolve);
    }));
    return sdkPromise;
  };

  class CartKlarna extends HTMLElement {
    static get observedAttributes() {
      return ['data-cart-total'];
    }

    connectedCallback() {
      if (!this.shadowRoot) {
        const template = this.querySelector('template');
        if (!template) return;
        this.attachShadow({ mode: 'open' }).append(template.content.cloneNode(true));
        this.shadowRoot.addEventListener('click', (event) => this.onClick(event), true);
      }
      this.render();
      this.retry = () => {
        if (this.dataset.state === 'unavailable') this.render();
      };
      window.addEventListener('online', this.retry);
      this.drawer = this.closest('cart-drawer');
      this.drawerObserver = new MutationObserver(() => {
        if (this.drawer.classList.contains('active')) this.retry();
      });
      if (this.drawer) this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.unsubscribe();
      this.drawerObserver?.disconnect();
      window.removeEventListener('online', this.retry);
    }

    attributeChangedCallback(name, previous, current) {
      if (previous !== current && this.isConnected && this.shadowRoot) this.render();
    }

    unsubscribe() {
      if (this.messaging && this.onRendered) {
        this.messaging.off('placement-rendered', this.onRendered);
      }
      clearTimeout(this.readyTimer);
      clearTimeout(this.renderTimer);
    }

    async render() {
      const generation = this.generation = (this.generation || 0) + 1;
      this.unsubscribe();
      this.hidden = true;
      this.dataset.state = 'loading';
      this.placement = null;
      this.shadowRoot.querySelector('.ug-cart-klarna__message').replaceChildren();
      const amount = Number(this.dataset.cartTotal);
      if (!Number.isSafeInteger(amount) || amount <= 0) {
        this.dataset.state = 'empty';
        return;
      }
      const renderDeadline = Date.now() + 30000;
      this.renderTimer = setTimeout(() => {
        if (generation !== this.generation) return;
        this.dataset.state = 'unavailable';
      }, 30000);

      try {
        await loadSDK(this.dataset.clientId);
        if (!this.isConnected || generation !== this.generation) return;
        this.mountWhenReady(amount, generation, Math.max(renderDeadline, Date.now() + 5000));
      } catch (error) {
        // Leave checkout available without displaying an unverified installment amount.
        if (generation === this.generation) this.dataset.state = 'unavailable';
      }
    }

    mountWhenReady(amount, generation, deadline) {
      if (!this.isConnected || generation !== this.generation) return;
      const messaging = window.Klarna?.OnsiteMessaging;
      if (!messaging?.on || !messaging?.off) {
        // The SDK can define its element before exposing this API, even after the UI timeout.
        if (Date.now() >= deadline) {
          this.dataset.state = 'unavailable';
          clearTimeout(this.renderTimer);
          return;
        }
        this.readyTimer = setTimeout(() => this.mountWhenReady(amount, generation, deadline), 250);
        return;
      }

      const placement = document.createElement('klarna-placement');
      placement.dataset.key = 'credit-promotion-auto-size';
      placement.dataset.locale = 'de-DE';
      placement.dataset.theme = 'default';
      placement.dataset.purchaseAmount = String(amount);
      this.placement = placement;
      this.messaging = messaging;
      this.onRendered = (detail) => {
        if (generation !== this.generation || detail.element?.host !== placement) return;
        clearTimeout(this.renderTimer);
        this.hidden = !detail.content;
        this.dataset.state = detail.content ? 'ready' : 'unavailable';
      };
      messaging.on('placement-rendered', this.onRendered);

      // The Shopify Klarna App updates every light-DOM placement with the PDP price.
      // A shadow root isolates this cart amount without registering a global cart updater.
      // Recreate on total changes: an in-flight SDK request may otherwise return an old quote.
      this.shadowRoot.querySelector('.ug-cart-klarna__message').append(placement);
    }

    onClick(event) {
      const cta = event.composedPath().find((node) => node instanceof Element && node.part?.contains('osm-cta'));
      if (!cta) return;
      window.Shopify?.analytics?.publish?.('ga4Event', {
        log_name: '购物车 Klarna 分期说明点击:',
        event_name: 'click_button',
        event_parameters: { module: 'Cart Drawer', button_name: 'Klarna Placement' }
      });
    }
  }

  customElements.define('ug-cart-klarna', CartKlarna);
})();
